<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
              <template v-slot:button-content>
                <i class="mc-fa icon-outline_download"></i>{{ $t('global.action.download') }}
              </template>
              <b-dropdown-item href="#" @click="downloadReport('xlsx')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group id="driver_label" :label="$t('report.fields.driver')" label-for="driver">
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ driverLabel }}</span>
              <McBadge
                v-if="driverIdCount > 2"
                :text="`+${driverIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="false"
      reportKey="driver-score"
      v-if="Object.keys(filters).length > 0"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <TableViewManager
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(driver)="data">
        {{ data.item.driver ? data.item.driver.fullname : '-' }}
      </template>

      <template #cell(totalDriven)="data">
        {{ formatNumber(minimizeFloat(data.item.totalDriven ?? 0)) }}
      </template>

      <template #cell(totalBonus)="data">
        {{ formatNumber(minimizeFloat(data.item.totalBonus ?? 0)) }}
      </template>

      <template #cell(scoreOverspeed)="data">
        {{ formatNumber(minimizeFloat(data.item.overspeed.score ?? 0)) }}
      </template>

      <template #cell(scoreAccel)="data">
        {{ formatNumber(minimizeFloat(data.item.harshAccel.score ?? 0)) }}
      </template>

      <template #cell(scoreBrake)="data">
        {{ formatNumber(minimizeFloat(data.item.harshBrake.score ?? 0)) }}
      </template>

      <template #cell(scoreSharpturn)="data">
        {{ formatNumber(minimizeFloat(data.item.sharpturn.score ?? 0)) }}
      </template>

      <template #cell(overspeeds)="data">
        {{ data.item.overspeed.total ?? 0 }}
      </template>

      <template #cell(harshAccels)="data">
        {{ data.item.harshAccel.total ?? 0 }}
      </template>

      <template #cell(harshBrakes)="data">
        {{ data.item.harshBrake.total ?? 0 }}
      </template>

      <template #cell(sharpTurns)="data">
        {{ data.item.sharpturn.total ?? 0 }}
      </template>

      <template #cell(score)="data">
        {{ formatNumber(minimizeFloat(data.item.score ?? 0)) }}
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import TableViewManager from '../../components/TableViewManager.vue'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import McBadge from '@/components/McBadge'
import { GET_REPORT_DRIVER_SCORES, DRIVER_SCORE_REPORT } from '@/services/api/report/report'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { MixPanel } from '@/mixin/mixpanel'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    TableViewManager,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'driver-score',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
    }
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    childFields() {
      const filteredData = this.fields.filter((obj) => {
        return Object.prototype.hasOwnProperty.call(obj, 'children')
      })

      return filteredData
    },
    fields() {
      return this.reportFields
    },
    columns() {
      const finalFields = []

      this.reportFields.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(field, 'children')) {
          finalFields.push(...field.children)
        } else {
          finalFields.push(field)
        }
      })
      return finalFields.map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, grade, ...otherFilter } = filters
        const month = periode.length > 0 ? this.momentM(periode[1]) : null
        const year = periode.length > 0 ? this.momentY(periode[1]) : null
        const finalGrade = grade ? grade.map((g) => this.getIndexFilter(g)) : undefined

        const model = {
          month,
          year,
          finalGrade: finalGrade,
          ...otherFilter,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.isBusy = true
          filter.orderBy = JSON.stringify({ fullname: 'asc' })
          const res = await this.queryCache.fetch(
            GET_REPORT_DRIVER_SCORES,
            {
              filter: {
                ...model,
                ...filter,
              },
            },
            self.reportKey
          )
          const result = res.driverScores.driverScores
          self.totalRows = result?.pagination?.total ?? 0
          self.isBusy = false
          self.message = res.driverScores.message
          self.isError = !res.driverScores.isSuccessful
          self.modalVerifReport += 1
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    
    driverLabel() {
      if (this.filters.driverId && this.filters.driverId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.driver : '-'
      } else {
        return '-'
      }
    },
    driverIdCount() {
      return this.filters.driverId && this.filters.driverId.length > 0
        ? this.filters.driverId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
  },
  methods: {
    formatNumber(input) {
      // Convert the number to a string and replace the dot with a comma
      let formattedNumber = input.toString().replace('.', ',')

      // Add dots as thousands separators to the whole part
      formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

      return formattedNumber
    },
    getIndexFilter(id) {
      switch (id) {
        case 0:
          return 'A'
        case 1:
          return 'B'
        case 2:
          return 'C'
        case 3:
          return 'D'
        case 4:
          return 'E'
        default:
          return 'E'
      }
    },
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.driverScoresReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        column: this.columns,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: extension === 'pdf' ? 'pdf' : 'excel',
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: DRIVER_SCORE_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.driverScoresReport)
        })
    },
  },
}
</script>
